// Maps the pre-signup Funder Get Started quiz draft (localStorage
// `clinq_funder_get_started_v1`) onto the /funder/mandate schema so the
// funder's terminal filters are pre-filled the moment they sign up.

export const FUNDER_QUIZ_STORAGE_KEY = "clinq_funder_get_started_v1";

const SECTOR_TO_MANDATE = {
  agriculture: "Agritech",
  healthcare: "Healthtech",
  financial: "Fintech",
  manufacturing: "Manufacturing",
  retail: "E-commerce",
  logistics: "Logistics",
  energy: "Cleantech",
  real_estate: "Real Estate",
  tech_saas: "SaaS",
  education: "Edtech",
  hospitality: "Hospitality",
};

const TICKET_BANDS = {
  lt_50k: [0, 50000],
  "50k_250k": [50000, 250000],
  "250k_1m": [250000, 1000000],
  "1m_5m": [1000000, 5000000],
  "5m_15m": [5000000, 15000000],
  gt_15m: [15000000, 100000000],
};

const EAST_AFRICA = ["Kenya", "Rwanda", "Ethiopia", "Tanzania", "Uganda"];
const ALL_COUNTRIES = ["Nigeria", "Kenya", "Ghana", "South Africa", "Egypt", "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda"];

export function readFunderQuizDraft() {
  try {
    const raw = localStorage.getItem(FUNDER_QUIZ_STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : null;
  } catch (_) { return null; }
}

const RISK_TO_STRUCTURES = {
  bold: ["equity", "convertible", "open"],
  balanced: ["equity", "debt", "mezzanine", "mix"],
  steady: ["debt", "mix"],
};

export function draftToMandate(draft) {
  if (!draft) return null;
  const sectors = [];
  for (const s of draft.sectors || []) {
    if (s === "agnostic") continue; // agnostic → empty list = match all
    const mapped = SECTOR_TO_MANDATE[s];
    if (mapped && !sectors.includes(mapped)) sectors.push(mapped);
  }
  const otherSector = (draft.sectorOther || "").trim();
  if (otherSector && !sectors.includes(otherSector)) sectors.push(otherSector.slice(0, 30));

  const [ticket_min, ticket_max] = TICKET_BANDS[draft.ticket] || [50000, 3000000];

  // Stage question replaced by deal-focus (business vs project) Aug 2026 —
  // legacy drafts may still carry `stages`; map what we can, else match all.
  const legacyStageMap = { idea_pre_revenue: ["Idea", "MVP"], early_revenue: ["Revenue"],
    seed: ["Revenue"], series_a: ["Growth"], series_b: ["Growth"],
    mature: ["Growth"], turnaround: ["Growth"] };
  const stages = [];
  for (const st of draft.stages || []) {
    for (const m of legacyStageMap[st] || []) {
      if (!stages.includes(m)) stages.push(m);
    }
  }

  const countries = [];
  const geo = draft.geos || [];
  const addAll = (list) => list.forEach((c) => { if (!countries.includes(c)) countries.push(c); });
  if (geo.includes("pan_african")) addAll(ALL_COUNTRIES);
  else {
    if (geo.includes("kenya_only")) addAll(["Kenya"]);
    if (geo.includes("east_africa")) addAll(EAST_AFRICA);
    if (geo.includes("specific")) {
      (draft.geoSpecific || "").split(/[,;]/).map((c) => c.trim()).filter(Boolean)
        .forEach((c) => {
          const match = ALL_COUNTRIES.find((k) => k.toLowerCase() === c.toLowerCase());
          const val = match || c.slice(0, 30);
          if (!countries.includes(val)) countries.push(val);
        });
    }
  }

  return {
    sectors,
    countries,
    ticket_min,
    ticket_max,
    sdgs: [],
    stages,
    min_readiness: 0,
    instruments: (draft.structures && draft.structures.length
      ? draft.structures
      : (RISK_TO_STRUCTURES[draft.riskAppetite] || [])),
    risk_appetite: ["bold", "balanced", "steady"].includes(draft.riskAppetite)
      ? draft.riskAppetite : null,
    investor_type: draft.investorType === "institution" ? "institution" : "individual",
    deal_focus: ["businesses", "projects", "both"].includes(draft.dealFocus) ? draft.dealFocus : null,
  };
}
